import { fetchApi } from '@/modules/sanr-api';
import { track } from './track';

// Arena Setup checklist / onboarding state, persisted server-side as
// `issuer.tutorials` ({key: true} map on the profile). Every key is latched
// forever via POST /v1/issuer/tutorials — deliberately including `eligible`,
// which a live-data read could later flip back off (accuracy drops below the
// threshold); a checklist item must never un-check. The API has no un-mark
// (POST body is `{tutorialKey}` only — sets true).
export type TutorialKey =
  | 'onboarding_completed'
  // Suppresses the welcome auto-show WITHOUT ticking the checklist item —
  // the Arena Setup "Complete" button stays as the tour re-entry.
  | 'onboarding_skipped'
  | 'first_signal'
  | 'first_stake'
  | 'eligible'
  | 'arena_setup_finished';

// Marked-this-session (or in flight) — avoids duplicate POSTs from repeated
// fire points; the endpoint is idempotent server-side anyway.
const marked = new Set<TutorialKey>();

// The dedupe set is per-USER state at module scope: clearStoredSession
// resets it on disconnect, or it would survive an account switch and skip
// the next account's tutorial POSTs.
export function clearMarkedTutorials(): void {
  marked.clear();
}

// Best-effort: swallows errors (a failed mark must never break the calling
// flow; the key retries on the next trigger). `already` (profile.tutorials)
// skips keys the server already holds.
export async function markTutorial(
  key: TutorialKey,
  already?: Record<string, boolean>,
): Promise<void> {
  if (already?.[key] === true || marked.has(key)) return;
  marked.add(key);
  try {
    await fetchApi('POST_ISSUER_TUTORIALS', {
      signal: undefined,
      body: { tutorialKey: key },
    });
    track('tutorial_marked', { key });
  } catch {
    marked.delete(key);
  }
}
